import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import { NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useNavigationAnalyticsContext } from './navigation-analytics-context';
import {
  SENTINEL_PARAM,
  isOnSentinelEntry,
  readHistoryIdx,
  searchHasSentinel
} from './navigation-sentinel';

interface NavigationBlockContextValue {
  // While any blocker is registered, browser Back closes instead of navigating: every registered
  // onBlocked runs, top-most first.
  addBlocker: (key: string, onBlocked: () => void) => void;
  removeBlocker: (key: string) => void;
}

const NavigationBlockContext = createContext<NavigationBlockContextValue>({
  addBlocker: () => undefined,
  removeBlocker: () => undefined
});

// Back-close runs entirely on a sentinel history entry, never on react-router's useBlocker POP
// interception — that path is unreliable (it can swallow the first POP after a blocker arms, and it
// cannot tell two adjacent entries apart when they share a URL). The first blocker to register
// pushes ONE sentinel entry; later ones just join the set. A single Back pops that sentinel and
// closes the WHOLE stack, so one sentinel covers the entire set and never needs re-arming — a Back
// always leaves the user with nothing open.
const NavigationBlockContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const blockersRef = useRef<Map<string, () => void>>(new Map());
  const sentinelActiveRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { ignoreNextNavigation } = useNavigationAnalyticsContext();

  const locationRef = useRef(location);
  useLayoutEffect(() => {
    locationRef.current = location;
  });

  // Back clears the whole set, and nothing may survive it. Enforced here rather than asking each
  // consumer to cooperate: a back-close runs the same onClose the ✕ runs, so a parent that re-opens
  // itself when its child closes (the bank-account disclosure behind Plaid/manual entry, which
  // predates this work) cannot tell the two apart and puts itself straight back on screen. Anything
  // that registers inside this window is closed immediately.
  //
  // It also has to hold for a second reason. Chrome's history-manipulation intervention flags a
  // pushState with no user activation behind it and lets Back skip the entire run of flagged entries
  // — ejecting the user from the funnel instead of closing a modal. A teardown re-render has no
  // gesture anywhere in its task, so arming a sentinel there mints exactly such an entry.
  //
  // The window is "since the back-close, with no user interaction" — so only effect-driven re-opens
  // land in it. A modal the user actually opened arrives after a pointerdown/keydown and is
  // untouched.
  const backCloseUnwindingRef = useRef(false);
  useEffect(() => {
    const onInteraction = () => {
      backCloseUnwindingRef.current = false;
    };
    window.addEventListener('pointerdown', onInteraction, true);
    window.addEventListener('keydown', onInteraction, true);
    return () => {
      window.removeEventListener('pointerdown', onInteraction, true);
      window.removeEventListener('keydown', onInteraction, true);
    };
  }, []);

  // The router runs under a basename ('/purchase'), so sentinel paths are built from react-router's
  // location, not window.location.
  const buildPath = useCallback((withSentinel: boolean) => {
    const loc = locationRef.current;
    const params = new URLSearchParams(loc.search);
    if (withSentinel) params.set(SENTINEL_PARAM, 'true');
    else params.delete(SENTINEL_PARAM);
    const query = params.toString();
    return `${loc.pathname}${query ? `?${query}` : ''}${loc.hash}`;
  }, []);

  const pushSentinel = useCallback(() => {
    // A reload (or restored tab) can land us on a URL that still carries the marker while no guard
    // is armed. Pushing on top of it would make the sentinel and the real entry share a URL again —
    // the exact ambiguity this marker exists to prevent — so strip the stale one first.
    if (isOnSentinelEntry()) navigate(buildPath(false), { replace: true });
    sentinelActiveRef.current = true;
    navigate(buildPath(true), { state: locationRef.current.state as unknown });
  }, [buildPath, navigate]);

  useEffect(() => {
    const onPop = () => {
      // removeBlocker clears the flag before its own navigate(-1), so that cleanup POP no-ops here.
      if (!sentinelActiveRef.current) return;
      // Still sitting on the sentinel — this POP isn't the Back we're guarding against.
      if (isOnSentinelEntry()) return;

      // Back consumed the sentinel and closes the whole stack, so nothing is left to guard. Cleared
      // BEFORE the handlers run: closing releases each guard, and removeBlocker must not read a
      // still-active sentinel and try to consume it again with navigate(-1).
      sentinelActiveRef.current = false;

      const blockers = [...blockersRef.current.values()];
      if (blockers.length === 0) return;

      // Set before the handlers run, so anything that re-opens in the commit their onClose schedules
      // lands inside the window. Only Back sets it — a manual close leaves it false.
      backCloseUnwindingRef.current = true;

      // Top-most first (Map preserves insertion order) — nested modals tear down before the parent
      // that renders them.
      blockers.reverse().forEach(onBlocked => {
        onBlocked();
      });
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  // Navigating forward out of an open modal strands its sentinel in history (removeBlocker only
  // consumes one the user is still sitting on). Landing ON it spends a Back press for nothing: the
  // entry renders identically to the one behind it, so Back looks like it did nothing and the user
  // has to press again (TXCC-9811). Carry them the rest of the way.
  //
  // Guarded on idx > 0: at the bottom of react-router's history there may be no app entry behind,
  // and navigate(-1) would eject the user out of the funnel instead of skipping a marker. Back only —
  // a Forward that landed here would be carried past the entry it actually asked for.
  const prevLocationRef = useRef(location);
  const prevIdxRef = useRef<number>(readHistoryIdx() ?? 0);
  useEffect(() => {
    const prev = prevLocationRef.current;
    prevLocationRef.current = location;
    const prevIdx = prevIdxRef.current;
    const idx = readHistoryIdx();
    if (idx !== undefined) prevIdxRef.current = idx;
    if (prev === location || navigationType !== NavigationType.Pop) return;
    if (idx === undefined || idx === 0 || idx > prevIdx) return;
    if (sentinelActiveRef.current || !searchHasSentinel(location.search)) return;
    ignoreNextNavigation();
    navigate(-1);
  }, [location, navigationType, navigate, ignoreNextNavigation]);

  const addBlocker = useCallback(
    (key: string, onBlocked: () => void) => {
      const hadBlockers = blockersRef.current.size > 0;
      blockersRef.current.set(key, onBlocked);

      // Registering inside the back-close window means an effect re-opened this modal on the very
      // press meant to clear the set. Close it straight back and arm nothing — no sentinel is minted,
      // so there is no gesture-less history entry either.
      if (backCloseUnwindingRef.current) {
        onBlocked();
        return;
      }

      if (!hadBlockers && !sentinelActiveRef.current) pushSentinel();
    },
    [pushSentinel]
  );

  const removeBlocker = useCallback(
    (key: string) => {
      if (!blockersRef.current.delete(key)) return;

      // Deferred because a modal→modal handoff (autopay's disclosure closing as it opens Plaid)
      // passes through zero blockers: both setStates land in ONE commit, and React runs every effect
      // cleanup before any effect, so the replacement registers only after this cleanup. Consuming
      // synchronously here read that gap as "the last modal closed" and fired a navigate(-1) that the
      // replacement's own sentinel then absorbed as a user Back, closing the modal the instant it
      // opened. A microtask lets the same commit finish registering first.
      queueMicrotask(() => {
        if (blockersRef.current.size > 0) return;

        // Last blocker really did go. Consume the sentinel only if a manual (non-Back) close left us
        // sitting on it: if Back already popped it, onPop cleared the flag; if the app navigated on,
        // the URL marker is gone. In either case navigate(-1) would eat a real entry.
        const shouldConsume = sentinelActiveRef.current && isOnSentinelEntry();
        // Clear first so the navigate(-1) popstate below doesn't re-trigger the close listener.
        sentinelActiveRef.current = false;
        if (shouldConsume) {
          ignoreNextNavigation();
          navigate(-1);
        }
      });
    },
    [ignoreNextNavigation, navigate]
  );

  return (
    <NavigationBlockContext.Provider value={{ addBlocker, removeBlocker }}>
      {children}
    </NavigationBlockContext.Provider>
  );
};

const useNavigationBlockContext = () => useContext(NavigationBlockContext);

export { NavigationBlockContextProvider, useNavigationBlockContext };
export type { NavigationBlockContextValue };
